import { fetchChartingMobileOhlcSeries } from '@/features/charting-next/api/chartingInsightsRepository';

export const buildChartingMobileSeriesKey = (ticker, resolution = 'M') => {
    const normalizedTicker = String(ticker || '')
        .trim()
        .toUpperCase();
    const normalizedResolution = ['D', 'W', 'M'].includes(resolution) ? resolution : 'M';

    return normalizedTicker ? `${normalizedTicker}:${normalizedResolution}` : '';
};

const createResourceState = (key, status = 'idle', data = null, error = null) => ({
    key,
    status,
    data,
    error
});

export class ChartingMobileSeriesController {
    constructor({ loader = fetchChartingMobileOhlcSeries, maxConcurrent = 3, maxCacheEntries = 15, onChange = () => {} } = {}) {
        this.loader = loader;
        this.maxConcurrent = Math.max(1, Number(maxConcurrent) || 3);
        this.maxCacheEntries = Math.max(this.maxConcurrent, Number(maxCacheEntries) || 15);
        this.onChange = onChange;
        this.cache = new Map();
        this.pending = new Map();
        this.queue = [];
        this.activeCount = 0;
        this.destroyed = false;
    }

    stateFor(ticker, resolution = 'M') {
        const key = buildChartingMobileSeriesKey(ticker, resolution);
        const entry = key ? this.cache.get(key) : null;

        if (entry) {
            entry.lastAccessedAt = Date.now();
            return createResourceState(key, 'success', entry.data, null);
        }

        if (this.pending.has(key)) {
            return createResourceState(key, 'loading');
        }

        return createResourceState(key);
    }

    request(ticker, resolution = 'M', { force = false } = {}) {
        const key = buildChartingMobileSeriesKey(ticker, resolution);

        if (!key || this.destroyed) {
            return Promise.resolve(null);
        }

        if (force) {
            this.cache.delete(key);
        } else if (this.cache.has(key)) {
            const entry = this.cache.get(key);
            entry.lastAccessedAt = Date.now();
            this.emit(key, 'success', entry.data);
            return Promise.resolve(entry.data);
        }

        if (this.pending.has(key)) {
            return this.pending.get(key).promise;
        }

        let resolveRequest;
        let rejectRequest;
        const promise = new Promise((resolve, reject) => {
            resolveRequest = resolve;
            rejectRequest = reject;
        });
        const [normalizedTicker, normalizedResolution] = key.split(':');
        const task = {
            key,
            ticker: normalizedTicker,
            resolution: normalizedResolution,
            promise,
            resolve: resolveRequest,
            reject: rejectRequest
        };

        this.pending.set(key, task);
        this.queue.push(task);
        this.emit(key, 'loading');
        this.drain();

        return promise;
    }

    retry(ticker, resolution = 'M') {
        return this.request(ticker, resolution, { force: true });
    }

    emit(key, status, data = null, error = null) {
        if (!this.destroyed) {
            this.onChange(key, createResourceState(key, status, data, error));
        }
    }

    drain() {
        while (!this.destroyed && this.activeCount < this.maxConcurrent && this.queue.length) {
            const task = this.queue.shift();
            this.activeCount += 1;

            Promise.resolve(this.loader(task.ticker, task.resolution))
                .then((data) => {
                    if (this.destroyed) return;
                    this.cache.set(task.key, {
                        data,
                        lastAccessedAt: Date.now()
                    });
                    this.pruneCache();
                    this.emit(task.key, 'success', data);
                    this.complete(task);
                    task.resolve(data);
                })
                .catch((error) => {
                    if (this.destroyed) return;
                    this.emit(task.key, 'error', null, error);
                    this.complete(task);
                    task.reject(error);
                });
        }
    }

    complete(task) {
        this.activeCount = Math.max(0, this.activeCount - 1);
        this.pending.delete(task.key);
        this.drain();
    }

    pruneCache() {
        if (this.cache.size <= this.maxCacheEntries) return;

        [...this.cache.entries()]
            .sort((left, right) => left[1].lastAccessedAt - right[1].lastAccessedAt)
            .slice(0, this.cache.size - this.maxCacheEntries)
            .forEach(([key]) => this.cache.delete(key));
    }

    destroy() {
        this.destroyed = true;
        this.pending.forEach((task) => task.resolve(null));
        this.queue.splice(0);
        this.pending.clear();
        this.cache.clear();
        this.onChange = () => {};
    }
}
